import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicyPage = () => {
  return (
    <div data-testid="privacy-policy-page" className="pt-20 min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 
            className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-4"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Privacy Policy
          </h1>
          <p className="text-slate-400">Last updated: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-invert prose-slate max-w-none">
            
            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
              <p className="text-slate-300 leading-relaxed">
                Nassau Engineering ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                information when you visit our website nassauengineering.ie.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                We may collect information about you in various ways, including:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li><strong className="text-white">Personal Data:</strong> Name, email address, phone number, company name when you submit our contact form</li>
                <li><strong className="text-white">Usage Data:</strong> Information about how you use our website, including pages visited and time spent</li>
                <li><strong className="text-white">Device Data:</strong> Information about your device, browser type, and IP address</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li>Respond to your enquiries and provide customer service</li>
                <li>Send you quotes and project information you've requested</li>
                <li>Improve our website and services</li>
                <li>Analyze website usage to enhance user experience</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">4. Cookies and Tracking</h2>
              <p className="text-slate-300 leading-relaxed">
                We use cookies and similar tracking technologies to track activity on our website 
                and hold certain information. For detailed information about the cookies we use, 
                please see our <Link to="/cookie-policy" className="text-blue-400 hover:text-blue-300">Cookie Policy</Link>.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">5. Data Sharing</h2>
              <p className="text-slate-300 leading-relaxed">
                We do not sell, trade, or rent your personal information to third parties. 
                We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4 mt-4">
                <li>With service providers who assist in our operations</li>
                <li>To comply with legal requirements or protect our rights</li>
                <li>In connection with a business transfer or merger</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">6. Data Retention</h2>
              <p className="text-slate-300 leading-relaxed">
                We retain your personal information only for as long as necessary to fulfill the 
                purposes for which it was collected, including to satisfy legal, accounting, or 
                reporting requirements.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">7. Your Rights</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                Under GDPR, you have the following rights:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li>Right to access your personal data</li>
                <li>Right to rectification of inaccurate data</li>
                <li>Right to erasure ("right to be forgotten")</li>
                <li>Right to restrict processing</li>
                <li>Right to data portability</li>
                <li>Right to object to processing</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">8. Data Security</h2>
              <p className="text-slate-300 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your 
                personal information against unauthorized access, alteration, disclosure, or 
                destruction.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">9. Contact Us</h2>
              <p className="text-slate-300 leading-relaxed">
                If you have questions about this Privacy Policy or wish to exercise your rights, 
                please contact us at:
              </p>
              <div className="mt-4 text-slate-300">
                <p><strong className="text-white">Nassau Engineering</strong></p>
                <p>Unit 4b, Canal Bank, Hume Ave</p>
                <p>Park West Industrial Park</p>
                <p>Dublin, D12 ARX3, Ireland</p>
                <p className="mt-2">Phone: (01) 630 1410</p>
                <p>Email: info@nassauengineering.ie</p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
